import React from 'react';
import { Drawer, Box, Paper, Typography, IconButton, Divider, Tooltip, InputBase, CircularProgress, Alert, Button, Stack, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseRounded from '@mui/icons-material/CloseRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import StopRounded from '@mui/icons-material/StopRounded';
import { buildApiUrl, tenantApiPrefix } from '../config';
import { formatRelativeTime } from '../utils/date';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { chatTokens } from '../theme/chatTokens';
import { trackWidgetInsightsOpened } from '../utils/analytics';
import OnboardingCoachmark from './OnboardingCoachmark';
import { markProductStep, useProductOnboarding } from '../hooks/useProductOnboarding';

type ChatMessage = {
  message_id: string;
  thread_id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content?: string | null;
  parts?: any | null;
  created_at: string;
  client_msg_id?: string | null;
};

export function WidgetInsightsDrawer({
  open,
  onClose,
  tenantId,
  dashboardId,
  widgetId,
  widgetTitle,
  workflowId,
  renderedAt,
  onboardingMode = false,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  dashboardId: string;
  widgetId: string;
  widgetTitle: string;
  workflowId?: string;  // If present, use Chat V2 endpoint
  renderedAt?: string | null;
  onboardingMode?: boolean;
}) {
  const theme = useTheme();
  const t = theme.palette.mode === 'light' ? chatTokens.light : chatTokens.dark;
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState('');
  const [streamText, setStreamText] = React.useState('');
  const [threadId, setThreadId] = React.useState<string | null>(null);
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [hasFirstToken, setHasFirstToken] = React.useState<boolean>(false);
  const controllerRef = React.useRef<AbortController | null>(null);
  const endRef = React.useRef<HTMLDivElement | null>(null);
  const streamingRef = React.useRef<boolean>(false);
  const awaitingPersistRef = React.useRef<boolean>(false);
  const sendBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const [sendBtnAnchor, setSendBtnAnchor] = React.useState<HTMLButtonElement | null>(null);
  const { shouldShow, currentStepId } = useProductOnboarding();
  // Show the send-nudge when the user is at the widget_summarized step (needs to click Send)
  const showSendNudge = shouldShow && currentStepId === 'widget_summarized' && open && !loading;
  // Imperatively resolve the anchor after the drawer content mounts.
  // The Drawer animation takes ~200ms to render content into the DOM, so we
  // poll briefly to catch the element once it's available.
  React.useEffect(() => {
    if (!showSendNudge) { setSendBtnAnchor(null); return; }
    let cancelled = false;
    const tryResolve = (attempts = 0) => {
      const el = document.querySelector<HTMLButtonElement>('[data-nudge="insights-send"]');
      if (el) {
        if (!cancelled) setSendBtnAnchor(el);
        return;
      }
      if (attempts < 8) {
        setTimeout(() => tryResolve(attempts + 1), 100);
      }
    };
    // First attempt after drawer open animation (~250ms)
    const t = setTimeout(() => tryResolve(), 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [showSendNudge, draft]);
  const [pendingUser, setPendingUser] = React.useState<{ text: string; clientMsgId: string } | null>(null);
  const hasPendingUserInHistory = React.useMemo(() => {
    if (!pendingUser) return false;
    const cid = String(pendingUser.clientMsgId || '');
    if (!cid) return false;
    return messages.some((m) => m.role === 'user' && m.client_msg_id && String(m.client_msg_id) === cid);
  }, [pendingUser, messages]);
  const messagesRef = React.useRef<ChatMessage[]>([]);
  const DEFAULT_SUMMARY_PROMPT = 'Summarize the key insights, trends, and notable patterns in this data.';

  // Pre-stream rotating status (UI-only)
  const preStreamSteps = React.useMemo(() => [
    'Reviewing the widget data',
    'Spotting patterns and trends',
    'Preparing the insights',
  ], []);
  const [preStreamIndex, setPreStreamIndex] = React.useState(0);
  React.useEffect(() => {
    if (loading && !hasFirstToken) {
      const id = window.setInterval(() => {
        setPreStreamIndex((i) => (i + 1) % preStreamSteps.length);
      }, 1600);
      return () => window.clearInterval(id);
    }
    setPreStreamIndex(0);
  }, [loading, hasFirstToken, preStreamSteps.length]);

  const fetchHistory = React.useCallback(async (tid?: string) => {
    // Fetch messages from widget insights thread
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      if (!token) return;
      
      const url = buildApiUrl(`${tenantApiPrefix(tenantId)}/chat-v2/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widgetId)}/insights/messages`);
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        if (data.messages && Array.isArray(data.messages)) {
          setMessages(data.messages);
          // Clear streaming text after messages are loaded
          if (awaitingPersistRef.current) {
            setStreamText('');
            awaitingPersistRef.current = false;
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch widget insights history:', err);
    }
  }, [dashboardId, widgetId, tenantId]);

  // Keep a ref of messages for async checks
  React.useEffect(() => { messagesRef.current = messages; }, [messages]);

  

  // Prefer backend API: load messages by widget id; no need to compute thread id on client

  const startSummaryStream = React.useCallback(async () => {
    if (streamingRef.current) return;
    setError(null);
    setStreamText('');
    setLoading(true);
    setHasFirstToken(false);
    if (controllerRef.current) {
      try { controllerRef.current.abort(); } catch {}
    }
    streamingRef.current = true;
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const url = buildApiUrl(`${tenantApiPrefix(tenantId)}/chat-v2/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widgetId)}/insights`);
      const ctrl = new AbortController();
      controllerRef.current = ctrl;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
        signal: ctrl.signal,
      } as RequestInit);
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(txt || 'Failed to start AI insights');
      }
      if (!resp.body) throw new Error('No response body');
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        for (const frame of frames) {
          const lines = frame.split('\n');
          const eventLine = lines.find((l) => l.startsWith('event:')) || 'event: message';
          const dataLine = lines.find((l) => l.startsWith('data:')) || 'data: {}';
          const type = eventLine.slice(6).trim();
          const json = dataLine.slice(5).trim();
          let data: any = {};
          try { data = json ? JSON.parse(json) : {}; } catch { data = {}; }
          if (type === 'meta' && data?.thread_id && !threadId) {
            setThreadId(String(data.thread_id));
            // Fetch history once thread is known
            fetchHistory(String(data.thread_id));
          } else if (type === 'token') {
            const t = (data && data.token) ? String(data.token) : '';
            if (t) {
              setStreamText((prev) => prev + t);
              if (!hasFirstToken) setHasFirstToken(true);
            }
          } else if (type === 'error') {
            setError(String(data || 'Stream error'));
          } else if (type === 'done') {
            setLoading(false);
            // Keep streaming text visible until history shows the assistant message
            awaitingPersistRef.current = true;
            if (threadId) setTimeout(() => fetchHistory(threadId), 800);
            setHasFirstToken(false);
            streamingRef.current = false;
            return;
          }
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load insights');
      setLoading(false);
      setHasFirstToken(false);
      streamingRef.current = false;
    }
  }, [tenantId, dashboardId, widgetId, workflowId, threadId, fetchHistory]);

  React.useEffect(() => {
    if (open) {
      trackWidgetInsightsOpened(dashboardId, widgetId);
      (async () => {
        // Load message history from insights thread (no auto-start)
        try {
          await fetchHistory();
          if (onboardingMode || (shouldShow && currentStepId === 'widget_summarized')) {
            setDraft((prev) => prev.trim() ? prev : DEFAULT_SUMMARY_PROMPT);
          }
          // Just load messages - user will click "Summarize again" if needed
        } catch (err) {
          console.error('Failed to load history:', err);
        }
      })();
    } else {
      // Reset state on close
      setError(null);
      setDraft('');
      setStreamText('');
      setThreadId(null);
      setMessages([]);
      setLoading(false);
      setHasFirstToken(false);
      setPendingUser(null);
      if (controllerRef.current) {
        try { controllerRef.current.abort(); } catch {}
      }
      streamingRef.current = false;
    }
  }, [open, startSummaryStream, tenantId, dashboardId, widgetId, workflowId, onboardingMode]);

  const sendFollowUp = React.useCallback(async (text?: string, isSummary?: boolean) => {
    if (streamingRef.current) return;
    const msg = (text ?? draft).trim();
    if (!msg && !isSummary) return;
    setDraft('');
    setError(null);
    setStreamText('');
    setLoading(true);
    setHasFirstToken(false);
    streamingRef.current = true;
    try {
      const normalizedDraft = msg.trim().toLowerCase();
      const normalizedDefault = DEFAULT_SUMMARY_PROMPT.trim().toLowerCase();
      const isSummaryIntent = Boolean(isSummary) || normalizedDraft === normalizedDefault;
      const token = window.localStorage.getItem('klaris_jwt');
      const url = buildApiUrl(`${tenantApiPrefix(tenantId)}/chat-v2/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widgetId)}/insights`);
      const clientMsgId = (typeof window !== 'undefined' && (window.crypto as any)?.randomUUID)
        ? (window.crypto as any).randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      setPendingUser({ text: msg || 'Summarize', clientMsgId });
      const ctrl = new AbortController();
      controllerRef.current = ctrl;
      
      // Build request body
      const requestBody: any = { client_msg_id: clientMsgId };
      if (isSummaryIntent) {
        requestBody.is_summary = true;
      } else if (msg) {
        requestBody.message = msg;
      }
      
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', Authorization: `Bearer ${token}` },
        body: JSON.stringify(requestBody),
        signal: ctrl.signal,
      } as RequestInit);
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(txt || 'Failed to send message');
      }
      if (!resp.body) throw new Error('No response body');
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        for (const frame of frames) {
          const lines = frame.split('\n');
          const eventLine = lines.find((l) => l.startsWith('event:')) || 'event: message';
          const dataLine = lines.find((l) => l.startsWith('data:')) || 'data: {}';
          const type = eventLine.slice(6).trim();
          const json = dataLine.slice(5).trim();
          let data: any = {};
          try { data = json ? JSON.parse(json) : {}; } catch { data = {}; }
          if (type === 'token') {
            const t = (data && data.token) ? String(data.token) : '';
            if (t) {
              setStreamText((prev) => prev + t);
              if (!hasFirstToken) setHasFirstToken(true);
            }
          } else if (type === 'error') {
            setError(String(data || 'Stream error'));
          } else if (type === 'done') {
            if (isSummaryIntent) {
              // Final onboarding step: mark only after summary completes.
              markProductStep('widget_summarized', { dashboard_id: dashboardId });
            }
            setLoading(false);
            // Keep streaming text visible until history shows the assistant message
            awaitingPersistRef.current = true;
            if (threadId) setTimeout(() => fetchHistory(threadId), 800);
            setHasFirstToken(false);
            streamingRef.current = false;
            return;
          }
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to send');
      setLoading(false);
      setHasFirstToken(false);
      streamingRef.current = false;
    }
  }, [tenantId, dashboardId, widgetId, workflowId, draft, threadId, fetchHistory]);

  const stopStreaming = React.useCallback(() => {
    try { controllerRef.current?.abort(); } catch {}
    setLoading(false);
    setHasFirstToken(false);
    streamingRef.current = false;
  }, []);

  // Auto-scroll: only if user hasn't manually scrolled up.
  // We use a ref so programmatic scrolls don't reset the flag.
  const userScrolledUpRef = React.useRef(false);
  const programmaticScrollRef = React.useRef(false);

  React.useEffect(() => {
    const container = endRef.current?.parentElement as HTMLElement | null;
    if (!container) return;
    const onScroll = () => {
      // Ignore scroll events triggered by our own scrollIntoView
      if (programmaticScrollRef.current) return;
      const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
      userScrolledUpRef.current = distance > 60;
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll to bottom only on new messages (not every token)
  React.useEffect(() => {
    if (!userScrolledUpRef.current) {
      programmaticScrollRef.current = true;
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
      // Reset flag after the scroll animation completes
      setTimeout(() => { programmaticScrollRef.current = false; }, 400);
    }
  }, [messages.length, loading]);

  // When loading starts (new question asked), always scroll to show the new question
  React.useEffect(() => {
    if (loading) {
      userScrolledUpRef.current = false;
      programmaticScrollRef.current = true;
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => { programmaticScrollRef.current = false; }, 400);
    }
  }, [loading]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520, md: 560 } } }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: t.panelBg }}>
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: t.panelBorder }}>
          <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{widgetTitle}</Typography>
            {renderedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Based on data refreshed {formatRelativeTime(renderedAt)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => sendFollowUp(undefined, true)}
              disabled={loading}
            >
              Summarize
            </Button>
            <IconButton size="small" onClick={onClose}>
              <CloseRounded />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ p: 1.5, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          {/* Historical messages (oldest → newest) */}
          {messages.map((m) => (
            m.role === 'user' ? (
              /* User messages: compact right-aligned bubble */
              <Box key={m.message_id} sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Paper elevation={0} sx={{ px: 1.5, py: 0.75, borderRadius: '12px 12px 4px 12px', bgcolor: t.userBg, maxWidth: '85%', border: '1px solid', borderColor: t.border }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.content || ''}</Typography>
                </Paper>
              </Box>
            ) : (
              /* Assistant messages: full-width card */
              <Box key={m.message_id} sx={{ mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: t.assistantBg, borderColor: t.border }}>
                  <Box sx={{
                    overflowX: 'auto',
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    WebkitOverflowScrolling: 'touch',
                    '& p': { m: 0 },
                    '& pre': { p: 1, borderRadius: 1, overflow: 'auto', maxWidth: '100%', bgcolor: t.codeBg },
                    '& code': { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
                    '& table': { display: 'block', width: 'max-content', maxWidth: 'none', minWidth: 640, borderCollapse: 'collapse', tableLayout: 'auto' },
                    '& th, & td': { border: `1px solid ${theme.palette.divider}`, padding: '4px 8px', verticalAlign: 'top', whiteSpace: 'nowrap', wordBreak: 'normal', overflowWrap: 'normal' },
                    '& img': { maxWidth: '100%', height: 'auto' }
                  }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {String(m.content || '')}
                    </ReactMarkdown>
                  </Box>
                </Paper>
              </Box>
            )
          ))}
          {/* Pending user bubble should appear after history and before streaming */}
          {pendingUser && !hasPendingUserInHistory && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Paper elevation={0} sx={{ px: 1.5, py: 0.75, borderRadius: '12px 12px 4px 12px', bgcolor: t.userBg, maxWidth: '85%', border: '1px solid', borderColor: t.border }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{pendingUser.text}</Typography>
              </Paper>
            </Box>
          )}
          {/* Streaming assistant message */}
          {(loading || streamText) && (
            <Box sx={{ mb: 1.25 }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: t.assistantBg, borderColor: t.border }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  Answer {loading && (<CircularProgress size={14} thickness={5} />)}
                </Typography>
                {loading && !hasFirstToken && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {preStreamSteps[preStreamIndex]}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ mb: 1.5 }} />
                {(hasFirstToken || !loading) && (
                  <Box sx={{
                    overflowX: 'auto',
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    WebkitOverflowScrolling: 'touch',
                    '& p': { m: 0 },
                    '& pre': { p: 1, borderRadius: 1, overflow: 'auto', maxWidth: '100%', bgcolor: t.codeBg },
                    '& code': { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
                    '& table': { display: 'block', width: 'max-content', maxWidth: 'none', minWidth: 640, borderCollapse: 'collapse', tableLayout: 'auto' },
                    '& th, & td': { border: `1px solid ${theme.palette.divider}`, padding: '4px 8px', verticalAlign: 'top', whiteSpace: 'nowrap', wordBreak: 'normal', overflowWrap: 'normal' },
                    '& img': { maxWidth: '100%', height: 'auto' }
                  }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {streamText}
                    </ReactMarkdown>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
          <div ref={endRef} />
        </Box>

        {/* Input */}
        <Box sx={{ p: 1.25, borderTop: '1px solid', borderColor: t.inputBorder }}>
          <Paper
            component="form"
            onSubmit={(e: any) => { e.preventDefault(); if (!loading) sendFollowUp(); }}
            variant="outlined"
            sx={{ p: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: t.inputBg, borderColor: t.inputBorder }}
          >
            <InputBase
              placeholder="Ask about this widget…"
              value={draft}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
              sx={{ ml: 1, flex: 1, fontSize: 14 }}
              disabled={loading}
            />
            {loading ? (
              <Tooltip title="Stop">
                <span>
                  <IconButton color="error" onClick={stopStreaming}>
                    <StopRounded />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <Tooltip title="Send">
              <span>
                <IconButton
                  ref={sendBtnRef}
                  data-nudge="insights-send"
                  type="submit"
                  color="primary"
                  disabled={loading || !draft.trim()}
                >
                  <SendRounded />
                </IconButton>
              </span>
            </Tooltip>
          </Paper>
          <OnboardingCoachmark
            open={Boolean(showSendNudge && sendBtnAnchor && draft.trim())}
            anchorEl={sendBtnAnchor}
            message="Click Send to generate the summary."
            placement="top"
            offsetX={-18}
            maxWidth={220}
          />
          {!loading && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {['Why did it change?', 'Top contributors', 'Any anomalies?'].map((s, i) => (
                <Chip
                  key={i}
                  label={s}
                  onClick={() => { setDraft(s); setTimeout(() => sendFollowUp(s), 0); }}
                  size="small"
                  sx={{ borderRadius: 1, bgcolor: t.chipBg, '&:hover': { bgcolor: t.chipHoverBg } }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default WidgetInsightsDrawer;


